import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import {
  Bot, Plus, Play, X, CheckCircle, XCircle, Loader2, Clock,
  ChevronDown, ChevronUp, Terminal, Server, RefreshCw, Trash2,
  AlertTriangle, FileText, Zap
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  pending_approval: { label: "Pending Approval", color: "bg-yellow-500/10 text-yellow-400 border-yellow-500/20", icon: Clock },
  running: { label: "Running", color: "bg-blue-500/10 text-blue-400 border-blue-500/20", icon: Loader2 },
  completed: { label: "Completed", color: "bg-green-500/10 text-green-400 border-green-500/20", icon: CheckCircle },
  failed: { label: "Failed", color: "bg-red-500/10 text-red-400 border-red-500/20", icon: XCircle },
  cancelled: { label: "Cancelled", color: "bg-zinc-500/10 text-zinc-400 border-zinc-500/20", icon: X },
};

const TASK_EXAMPLES = [
  "Check security vulnerabilities and fix common issues",
  "Diagnose why Odoo ERP service is not running and fix it",
  "Check disk usage, find large files, and clean up logs older than 30 days",
  "Check all running services and restart any that are stopped",
  "Analyze system performance issues — CPU, memory, and disk I/O",
  "Check and update all outdated packages",
];

function StepCard({ step, index }) {
  const [expanded, setExpanded] = useState(false);
  const hasOutput = step.stdout || step.stderr;

  return (
    <div className="border border-white/5 rounded-lg overflow-hidden" data-testid={`step-${index}`}>
      <button
        onClick={() => setExpanded(v => !v)}
        className="w-full flex items-center gap-3 px-3 py-2.5 bg-white/5 hover:bg-white/8 text-left transition-colors"
      >
        <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-mono font-bold shrink-0 ${
          step.exit_code === 0 ? "bg-green-500/20 text-green-400" :
          step.exit_code === -1 ? "bg-red-500/20 text-red-400" :
          "bg-yellow-500/20 text-yellow-400"
        }`}>{step.step}</span>
        <div className="flex-1 min-w-0">
          <code className="text-xs text-blue-300 font-mono truncate block">{step.command}</code>
          {step.reasoning && <p className="text-[10px] text-zinc-500 truncate">{step.reasoning}</p>}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className={`text-[10px] px-1.5 py-0.5 rounded font-mono ${
            step.exit_code === 0 ? "bg-green-500/10 text-green-400" : "bg-red-500/10 text-red-400"
          }`}>exit {step.exit_code}</span>
          {expanded ? <ChevronUp className="w-3.5 h-3.5 text-zinc-500" /> : <ChevronDown className="w-3.5 h-3.5 text-zinc-500" />}
        </div>
      </button>
      {expanded && hasOutput && (
        <div className="bg-black/60 p-3 space-y-2">
          {step.stdout && (
            <pre className="text-xs text-green-300 font-mono whitespace-pre-wrap overflow-x-auto max-h-64 overflow-y-auto">{step.stdout}</pre>
          )}
          {step.stderr && (
            <pre className="text-xs text-red-400 font-mono whitespace-pre-wrap overflow-x-auto max-h-32 overflow-y-auto">{step.stderr}</pre>
          )}
        </div>
      )}
    </div>
  );
}

function TaskDetailModal({ task, onClose, onApprove, onCancel, onDelete, currentUser }) {
  const isAdmin = currentUser?.role === "admin" || currentUser?.role === "senior_admin";
  const cfg = STATUS_CONFIG[task.status] || STATUS_CONFIG.pending_approval;
  const StatusIcon = cfg.icon;
  const isRunning = task.status === "running";

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-[#0c0c0e] border-white/10 text-white max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-mono flex items-center gap-2 text-lg">
            <Bot className="w-5 h-5 text-blue-400" />
            {task.title}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          {/* Status + Meta */}
          <div className="flex items-center gap-3 flex-wrap">
            <span className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs border font-medium ${cfg.color}`}>
              <StatusIcon className={`w-3.5 h-3.5 ${isRunning ? "animate-spin" : ""}`} />
              {cfg.label}
            </span>
            <span className="text-xs text-zinc-500">By {task.created_by_email}</span>
            <span className="text-xs text-zinc-500">{new Date(task.created_at).toLocaleString()}</span>
            <span className="text-xs text-zinc-500">Max {task.max_steps} steps · {task.current_step || 0} executed</span>
          </div>

          {/* Description */}
          <div className="bg-white/5 rounded-lg p-3">
            <p className="text-xs text-zinc-400 uppercase tracking-wider mb-1">Task Description</p>
            <p className="text-sm text-white">{task.description}</p>
          </div>

          {/* Plan Preview */}
          {task.plan_preview && task.status === "pending_approval" && (
            <div className="bg-blue-500/5 border border-blue-500/10 rounded-lg p-3">
              <p className="text-xs text-blue-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                <FileText className="w-3.5 h-3.5" /> AI Plan Preview
              </p>
              <p className="text-sm text-zinc-300 whitespace-pre-line">{task.plan_preview}</p>
            </div>
          )}

          {/* Execution Log */}
          {task.execution_log && task.execution_log.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs text-zinc-400 uppercase tracking-wider flex items-center gap-1.5">
                <Terminal className="w-3.5 h-3.5" /> Execution Log ({task.execution_log.length} steps)
              </p>
              <div className="space-y-1.5 max-h-80 overflow-y-auto pr-1">
                {task.execution_log.map((step, i) => (
                  <StepCard key={i} step={step} index={i} />
                ))}
              </div>
              {isRunning && (
                <div className="flex items-center gap-2 text-blue-400 text-xs py-2">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  Agent is running...
                </div>
              )}
            </div>
          )}

          {/* Final Report */}
          {task.final_report && (
            <div className={`rounded-lg p-3 border ${
              task.status === "completed"
                ? "bg-green-500/5 border-green-500/10"
                : "bg-red-500/5 border-red-500/10"
            }`}>
              <p className={`text-xs uppercase tracking-wider mb-2 ${
                task.status === "completed" ? "text-green-400" : "text-red-400"
              }`}>
                {task.status === "completed" ? "Final Report" : "Error / Failure"}
              </p>
              <p className="text-sm text-zinc-200 whitespace-pre-line">{task.final_report}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-2 pt-2 border-t border-white/5">
            {task.status === "pending_approval" && isAdmin && (
              <Button className="bg-green-600 hover:bg-green-700 text-white"
                onClick={() => onApprove(task.id)} data-testid="approve-task-btn">
                <Play className="w-4 h-4 mr-2" /> Approve & Run
              </Button>
            )}
            {["pending_approval", "running"].includes(task.status) && (
              <Button variant="outline" className="border-red-500/20 text-red-400 hover:bg-red-500/10"
                onClick={() => onCancel(task.id)} data-testid="cancel-task-btn">
                <X className="w-4 h-4 mr-2" /> Cancel
              </Button>
            )}
            {["completed", "failed", "cancelled"].includes(task.status) && currentUser?.role === "admin" && (
              <Button variant="outline" className="border-red-500/20 text-red-400 hover:bg-red-500/10"
                onClick={() => { onDelete(task.id); onClose(); }} data-testid="delete-task-btn">
                <Trash2 className="w-4 h-4 mr-2" /> Delete
              </Button>
            )}
            <Button variant="outline" className="border-white/10 text-white ml-auto" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function AgentTasks() {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [servers, setServers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [creating, setCreating] = useState(false);
  const pollRef = useRef(null);

  const [form, setForm] = useState({
    title: "", description: "", server_id: "", max_steps: 30
  });

  const fetchTasks = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await axios.get(`${API}/agent-tasks`);
      setTasks(res.data);
    } catch {
      if (!silent) toast.error("Failed to load tasks");
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    Promise.all([
      fetchTasks(),
      axios.get(`${API}/servers`).then(r => setServers(r.data.filter(s => s.ssh_configured)))
    ]).catch(() => {});
  }, [fetchTasks]);

  // Poll for live updates when a task is running
  useEffect(() => {
    const hasRunning = tasks.some(t => t.status === "running");
    if (hasRunning) {
      pollRef.current = setInterval(() => {
        fetchTasks(true);
        // also refresh selected task detail
        if (selectedTask?.status === "running") {
          axios.get(`${API}/agent-tasks/${selectedTask.id}`)
            .then(r => setSelectedTask(r.data))
            .catch(() => {});
        }
      }, 2500);
    }
    return () => clearInterval(pollRef.current);
  }, [tasks, selectedTask, fetchTasks]);

  const handleCreate = async () => {
    if (!form.title || !form.description || !form.server_id) {
      toast.error("Fill in all fields");
      return;
    }
    setCreating(true);
    try {
      const res = await axios.post(`${API}/agent-tasks`, form);
      setTasks(prev => [res.data, ...prev]);
      setCreateOpen(false);
      setForm({ title: "", description: "", server_id: "", max_steps: 30 });
      setSelectedTask(res.data);
      toast.success("Task created — review the AI plan and approve to run");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create task");
    } finally {
      setCreating(false);
    }
  };

  const handleApprove = async (taskId) => {
    try {
      await axios.post(`${API}/agent-tasks/${taskId}/approve`);
      toast.success("Task approved — agent is now running");
      await fetchTasks(true);
      const updated = await axios.get(`${API}/agent-tasks/${taskId}`);
      setSelectedTask(updated.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to approve task");
    }
  };

  const handleCancel = async (taskId) => {
    try {
      await axios.post(`${API}/agent-tasks/${taskId}/cancel`);
      toast.success("Task cancelled");
      await fetchTasks(true);
      if (selectedTask?.id === taskId) {
        const updated = await axios.get(`${API}/agent-tasks/${taskId}`);
        setSelectedTask(updated.data);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to cancel");
    }
  };

  const handleDelete = async (taskId) => {
    try {
      await axios.delete(`${API}/agent-tasks/${taskId}`);
      setTasks(prev => prev.filter(t => t.id !== taskId));
      toast.success("Task deleted");
    } catch {
      toast.error("Failed to delete task");
    }
  };

  const openTask = async (task) => {
    try {
      const res = await axios.get(`${API}/agent-tasks/${task.id}`);
      setSelectedTask(res.data);
    } catch {
      setSelectedTask(task);
    }
  };

  const isAdmin = user?.role === "admin" || user?.role === "senior_admin";

  return (
    <div className="p-6 space-y-6" data-testid="agent-tasks-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-mono text-white tracking-tight flex items-center gap-3">
            <Bot className="w-8 h-8 text-blue-400" /> AI Tasks
          </h1>
          <p className="text-zinc-400 text-sm mt-1">
            Give the AI a task — it plans, you approve, it executes autonomously
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => fetchTasks()}
            className="bg-white/5 border-white/10 hover:bg-white/10 text-white"
            data-testid="refresh-tasks-btn">
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
          <Button className="bg-blue-600 hover:bg-blue-700 text-white"
            onClick={() => setCreateOpen(true)} data-testid="new-task-btn">
            <Plus className="w-4 h-4 mr-2" /> New Task
          </Button>
        </div>
      </div>

      {/* Stats bar */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {Object.entries(STATUS_CONFIG).map(([status, cfg]) => {
          const count = tasks.filter(t => t.status === status).length;
          return (
            <div key={status} className={`p-3 rounded-lg border ${cfg.color} text-center`}>
              <p className="text-xl font-bold font-mono">{count}</p>
              <p className="text-xs opacity-70">{cfg.label}</p>
            </div>
          );
        })}
      </div>

      {/* Task List */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 text-blue-400 animate-spin" />
        </div>
      ) : tasks.length === 0 ? (
        <Card className="bg-[#0c0c0e] border-white/5">
          <CardContent className="py-16 text-center space-y-4">
            <Bot className="w-14 h-14 text-zinc-700 mx-auto" />
            <p className="text-lg font-medium text-white">No tasks yet</p>
            <p className="text-zinc-500 text-sm max-w-md mx-auto">
              Create a task in plain English — the AI will figure out all the commands needed and execute them after your approval.
            </p>
            <Button className="bg-blue-600 hover:bg-blue-700 mt-2" onClick={() => setCreateOpen(true)}>
              <Plus className="w-4 h-4 mr-2" /> Create First Task
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {tasks.map(task => {
            const cfg = STATUS_CONFIG[task.status] || STATUS_CONFIG.pending_approval;
            const StatusIcon = cfg.icon;
            const server = servers.find(s => s.id === task.server_id);
            return (
              <Card key={task.id}
                className="bg-[#0c0c0e] border-white/5 hover:border-white/10 transition-all cursor-pointer"
                onClick={() => openTask(task)}
                data-testid={`task-card-${task.id}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between gap-2">
                    <CardTitle className="text-base font-medium text-white line-clamp-1">{task.title}</CardTitle>
                    <span className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] border whitespace-nowrap font-medium ${cfg.color}`}>
                      <StatusIcon className={`w-3 h-3 ${task.status === "running" ? "animate-spin" : ""}`} />
                      {cfg.label}
                    </span>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <p className="text-xs text-zinc-400 line-clamp-2">{task.description}</p>
                  <div className="flex items-center gap-4 text-xs text-zinc-500">
                    <span className="flex items-center gap-1">
                      <Server className="w-3 h-3" />
                      {server?.name || task.server_id.slice(0, 8)}
                    </span>
                    <span className="flex items-center gap-1">
                      <Zap className="w-3 h-3" />
                      {task.current_step || 0}/{task.max_steps} steps
                    </span>
                    <span>{new Date(task.created_at).toLocaleDateString()}</span>
                  </div>
                  {task.status === "pending_approval" && isAdmin && (
                    <Button size="sm"
                      className="w-full bg-green-600/20 hover:bg-green-600/30 text-green-400 border border-green-500/20 text-xs h-7"
                      onClick={(e) => { e.stopPropagation(); handleApprove(task.id); }}
                      data-testid={`quick-approve-${task.id}`}>
                      <Play className="w-3 h-3 mr-1" /> Approve & Run
                    </Button>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create Task Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="bg-[#0c0c0e] border-white/10 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-mono flex items-center gap-2">
              <Bot className="w-5 h-5 text-blue-400" /> New AI Task
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Examples */}
            <div className="space-y-1.5">
              <Label className="text-zinc-400 text-xs">Quick Examples</Label>
              <div className="flex flex-wrap gap-1.5">
                {TASK_EXAMPLES.slice(0, 3).map((ex, i) => (
                  <button key={i}
                    onClick={() => setForm(f => ({ ...f, description: ex, title: ex.slice(0, 50) }))}
                    className="text-[10px] px-2 py-1 bg-white/5 hover:bg-white/10 border border-white/10 rounded-md text-zinc-400 hover:text-white transition-colors text-left">
                    {ex}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-zinc-400 text-xs">Task Title</Label>
              <Input value={form.title}
                onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                placeholder="e.g. Fix Odoo ERP Issues"
                className="bg-black/50 border-white/10 text-white"
                data-testid="task-title-input" />
            </div>

            <div className="space-y-2">
              <Label className="text-zinc-400 text-xs">Describe what you want done</Label>
              <Textarea value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                placeholder="e.g. Check why Odoo ERP is not running on the server. Diagnose the issue, fix it, and confirm the service is back online."
                className="bg-black/50 border-white/10 text-white resize-none h-24"
                data-testid="task-description-input" />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-zinc-400 text-xs">Target Server</Label>
                <Select value={form.server_id} onValueChange={v => setForm(f => ({ ...f, server_id: v }))}>
                  <SelectTrigger className="bg-black/50 border-white/10 text-white" data-testid="task-server-select">
                    <SelectValue placeholder="Select server..." />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0c0c0e] border-white/10">
                    {servers.length === 0 ? (
                      <SelectItem value="none" disabled>No SSH servers configured</SelectItem>
                    ) : (
                      servers.map(s => (
                        <SelectItem key={s.id} value={s.id}>
                          <span className="text-xs">{s.name} ({s.ip_address})</span>
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-400 text-xs">Max Steps (1–100)</Label>
                <Input type="number" min={5} max={100} value={form.max_steps}
                  onChange={e => setForm(f => ({ ...f, max_steps: parseInt(e.target.value) || 30 }))}
                  className="bg-black/50 border-white/10 text-white"
                  data-testid="task-max-steps-input" />
              </div>
            </div>

            <div className="p-2.5 bg-blue-500/5 border border-blue-500/10 rounded-lg">
              <p className="text-[11px] text-blue-300">
                The AI will generate a plan preview for you to review before approving. 
                Once approved, it executes autonomously and gives you a final report.
              </p>
            </div>

            <div className="flex gap-2 pt-1">
              <Button variant="outline" className="flex-1 border-white/10 text-white"
                onClick={() => setCreateOpen(false)}>Cancel</Button>
              <Button className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                onClick={handleCreate} disabled={creating} data-testid="create-task-submit">
                {creating ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> AI Planning...</>
                ) : (
                  <><Bot className="w-4 h-4 mr-2" /> Create Task</>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Task Detail Modal */}
      {selectedTask && (
        <TaskDetailModal
          task={selectedTask}
          onClose={() => setSelectedTask(null)}
          onApprove={handleApprove}
          onCancel={handleCancel}
          onDelete={handleDelete}
          currentUser={user}
        />
      )}
    </div>
  );
}
